import { View, Text, styled, useTheme } from 'tamagui'
import { Volume2, Hash, MicOff, BellOff, ShieldAlert, Lock as LockIcon } from '@tamagui/lucide-icons'
import { Badge, Avatar } from '../../common'
import type { Channel, VoiceParticipant } from '@/types'
import { users } from '@/mocks'
import { useAppStore } from '@/store'
import { useState } from 'react'
import { ChannelPermissionsPopover } from './ChannelPermissionsPopover'

const Item = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  height: 32,
  paddingHorizontal: 8,
  marginHorizontal: 8,
  borderRadius: '$borderRadiusMd',
  cursor: 'pointer',
  gap: 6,
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
  variants: {
    active: {
      true: {
        backgroundColor: '$sidebarActive',
      },
    },
  } as const,
})

const ActiveBar = styled(View, {
  width: 2,
  height: 20,
  backgroundColor: '$accent',
  borderRadius: '$borderRadiusSm',
  marginRight: 2,
})

const ChannelName = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  flex: 1,
})

const VoiceParticipants = styled(View, {
  paddingLeft: 36,
  paddingRight: 8,
  marginHorizontal: 8,
  gap: 2,
  paddingBottom: 4,
})

const VoiceUser = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
  paddingVertical: 2,
})

const VoiceUsername = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$sidebarMuted',
})

interface ChannelItemProps {
  channel: Channel
  isActive: boolean
  onPress: () => void
  onJoinVoice?: (channelId: string) => void
  voiceParticipants?: VoiceParticipant[]
  onChannelDragStart?: (e: React.DragEvent, channelId: string, categoryId: string) => void
  onChannelDragOver?: (e: React.DragEvent, channelId: string) => void
  onChannelDrop?: (e: React.DragEvent, channelId: string) => void
  onChannelDragEnd?: () => void
  channelDragOverId?: string | null
  channelDragPosition?: 'above' | 'below' | null
  isDraggingChannel?: string | null
  serverId?: string
}

export function ChannelItem({ channel, isActive, onPress, onJoinVoice, voiceParticipants, onChannelDragStart, onChannelDragOver, onChannelDrop, onChannelDragEnd, channelDragOverId, channelDragPosition, isDraggingChannel, serverId }: ChannelItemProps) {
  const theme = useTheme()
  const isVoice = channel.type === 'voice'
  const hasParticipants = voiceParticipants && voiceParticipants.length > 0
  const { mutedChannelIds, openContextMenu } = useAppStore()
  const isMuted = mutedChannelIds.includes(channel.id)
  const isBeingDragged = isDraggingChannel === channel.id
  const [itemHovered, setItemHovered] = useState(false)
  const [showPerms, setShowPerms] = useState(false)
  const participantCount = voiceParticipants?.length || 0
  const capacityRatio = channel.userLimit ? participantCount / channel.userLimit : 0

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    openContextMenu('channel', channel.id, e.clientX, e.clientY)
  }

  return (
    <View position="relative">
      {/* Drop indicator */}
      {channelDragOverId === channel.id && channelDragPosition === 'above' && (
        <View position="absolute" top={0} left={16} right={16} height={2} backgroundColor="$accent" borderRadius="$borderRadiusSm" zIndex={10} />
      )}
      {channelDragOverId === channel.id && channelDragPosition === 'below' && (
        <View position="absolute" bottom={0} left={16} right={16} height={2} backgroundColor="$accent" borderRadius="$borderRadiusSm" zIndex={10} />
      )}
      <Item
        active={isActive}
        onPress={() => { onPress(); if (isVoice && onJoinVoice) onJoinVoice(channel.id) }}
        // @ts-expect-error -- web event + box-shadow
        onContextMenu={handleContextMenu}
        onMouseEnter={() => setItemHovered(true)}
        onMouseLeave={() => { setItemHovered(false); setShowPerms(false) }}
        opacity={isBeingDragged ? 0.4 : (isMuted && !isActive ? 0.5 : 1)}
        style={isActive ? { boxShadow: theme.shadowGlow?.val || 'none', transition: 'background-color 150ms ease' } : undefined}
        draggable
        onDragStart={(e: any) => onChannelDragStart?.(e, channel.id, channel.categoryId)}
        onDragOver={(e: any) => onChannelDragOver?.(e, channel.id)}
        onDrop={(e: any) => onChannelDrop?.(e, channel.id)}
        onDragEnd={onChannelDragEnd}
      >
        {isActive && <ActiveBar />}
        <View width={20} alignItems="center" justifyContent="center">
          {isVoice ? <Volume2 size={16} color="$warning" /> : <Hash size={16} color="$color8" />}
        </View>
        {channel.nsfw && (
          <ShieldAlert size={12} color="$error" />
        )}
        <ChannelName
          color={isActive ? '$surfaceForeground' : !isMuted && channel.unreadCount > 0 ? '$surfaceForeground' : '$sidebarMuted'}
          fontWeight={!isMuted && channel.unreadCount > 0 || isActive ? '600' : '400'}
        >
          {channel.name}
        </ChannelName>
        {isVoice && channel.userLimit && (
          <Text
            fontFamily="$fontMono"
            fontSize={11}
            color={capacityRatio >= 1 ? '$error' : capacityRatio >= 0.8 ? '$warning' : '$sidebarMuted'}
          >
            {participantCount}/{channel.userLimit}
          </Text>
        )}
        {isMuted && (
          <BellOff size={12} color="$sidebarMuted" />
        )}
        {!isMuted && channel.mentionCount > 0 && (
          <Badge count={channel.mentionCount} variant="mention" />
        )}
        {!isMuted && channel.unreadCount > 0 && channel.mentionCount === 0 && (
          <View
            width={6}
            height={6}
            borderRadius="$borderRadiusSm"
            backgroundColor="$surfaceForeground"
          />
        )}
        {itemHovered && (
          <View position="relative">
            <View
              width={20}
              height={20}
              borderRadius="$borderRadiusSm"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              hoverStyle={{ backgroundColor: '$sidebarActive' }}
              onPress={(e: any) => { e.stopPropagation(); setShowPerms(!showPerms) }}
            >
              <LockIcon size={10} color="$sidebarMuted" />
            </View>
            {showPerms && serverId && (
              <View position="absolute" right={0} top="100%" zIndex={1000} marginTop={4}>
                <ChannelPermissionsPopover channelId={channel.id} serverId={serverId} />
              </View>
            )}
          </View>
        )}
      </Item>

      {isVoice && hasParticipants && (
        <VoiceParticipants>
          {voiceParticipants.map((p) => {
            const user = users[p.userId]
            if (!user) return null
            return (
              <VoiceUser key={p.userId}>
                <Avatar
                  name={user.displayName}
                  color={user.roleColor || '#2A3142'}
                  size="sm"
                  showStatus={false}
                  isSpeaking={p.isSpeaking}
                />
                <VoiceUsername
                  color={p.isSpeaking ? '$warning' : '$sidebarMuted'}
                >
                  {user.displayName}
                  {p.isMuted && <MicOff size={10} color="$sidebarMuted" style={{ marginLeft: 4 }} />}
                </VoiceUsername>
              </VoiceUser>
            )
          })}
        </VoiceParticipants>
      )}
    </View>
  )
}
